import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { isAxiosError } from 'axios';

interface DeleteFacebookPagePayload {
  id: number;
}

export interface DeleteFacebookPageResponse {
  message: string;
  details: string;
}

export const deleteFacebookPage = async (
  {
    id,
  }: DeleteFacebookPagePayload): Promise<DeleteFacebookPageResponse> => {
  const res = await apiClient.delete<DeleteFacebookPageResponse>(
    `${queryKeys.facebookPages}/${id}`,
  );

  if (isAxiosError(res)) {
    return handleAxiosError(res, 'Failed to delete facebook page');
  }

  return res.data;
};

type MutationFnType = (
  data: DeleteFacebookPagePayload,
) => ReturnType<typeof deleteFacebookPage>;

interface UseDeleteFacebookPageOptions {
  config?: MutationConfig<MutationFnType>;
}

export const useDeleteFacebookPage = (
  {
    config,
  }: UseDeleteFacebookPageOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.facebookPages],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: deleteFacebookPage,
  });
};
